<script setup lang="ts">
import { computed, onBeforeMount, onMounted, ref } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { formatDate } from '@/utils/dateUtils';
import { cloneDeep } from 'lodash';
import { useBookComposable } from '../useBookComposable';
import { useModalComposable } from '@/views/composable';
import { useDialog } from 'primevue/usedialog';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import InputNumber from 'primevue/inputnumber';
import TableColumn from 'primevue/column';
import AirPaxName from '../AirPaxName.vue';
import UpdatePassengerDetailModal from '@/components/modal/UpdatePassengerDetailModal.vue';

const props = defineProps<{
  isManagement?: boolean;
  dataFlightPax: any;
  dataFlightPrice: object;
  priorityStatus?: number | undefined;
}>();

const emits = defineEmits<{
  addPax: [value: any];
  submitCruiseQuote: [];
  'update:dataFlightPax': [value: any];
  'update:dataFlightPrice': [value: any];
}>();

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();
const dialog = useDialog();
const editingRows = ref([]);
const airEditingRows = ref([]);
const expandedRows = ref([]);
const data = ref<any>([]);
const { hasViewOccupacyPermission, hasEditBookPermission, checkPriorityStatus } = useBookComposable();
const { handleOpenErrorMessage } = useModalComposable();

const dataFlightPrice = computed({
  get() {
    return props.dataFlightPrice;
  },
  set(value) {
    emits('update:dataFlightPrice', value);
  }
});

const dataFlightPax = computed({
  get() {
    return props.dataFlightPax;
  },
  set(value) {
    emits('update:dataFlightPax', value);
  }
});

const getFlightIndex = (prices: any, priceId: string) => {
  return prices.findIndex((item: any) => item.id === priceId);
};

const validateMaxNumOfSeat = (numOfAdults: number, numOfChildren: number, totalNumOfAdults: number, totalNumOfChildren: number, maxPassengers: number, optionAdultNo: number, optionChdNo: number) => {
  if (numOfAdults > optionAdultNo || numOfChildren > optionChdNo || (maxPassengers && numOfAdults + numOfChildren > maxPassengers) || totalNumOfAdults > optionAdultNo || totalNumOfChildren > optionChdNo) {
    return false;
  }

  return true;
};

const onRowEditSaveFlight = ({ newData }, priceItems: any, dataCommon: any) => {
  let optionAdt = 0;
  let optionChd = 0;

  if (newData.type == 'Economy') {
    optionAdt = dataCommon?.flights?.[0]?.adt ?? 0;
    optionChd = dataCommon?.flights?.[0]?.chd ?? 0;
  } else {
    optionAdt = dataCommon?.flights?.[0]?.businessAdt ?? 0;
    optionChd = dataCommon?.flights?.[0]?.businessChd ?? 0;
  }

  if (newData.adtNo > optionAdt || newData.chdNo > optionChd) {
    return handleOpenErrorMessage(t('my_quote.toastMessage.over_selected_seat'));
  }

  const maxPassengers = newData.numberOfPassengers;

  let totalAdults = newData.adtNo;
  let totalChildren = newData.chdNo;
  (priceItems as any[]).forEach((priceItem) => {
    if (newData.id !== priceItem.id && newData.type === priceItem.type) {
      totalAdults += priceItem.adtNo;
      totalChildren += priceItem.chdNo;
    }
  });

  const validateOverSeat = validateMaxNumOfSeat(newData.adtNo, newData.chdNo, totalAdults, totalChildren, maxPassengers, optionAdt, optionChd);
  if (!validateOverSeat) {
    handleOpenErrorMessage(t('my_quote.toastMessage.over_selected_seat'));
  } else {
    const indexData = getFlightIndex(priceItems, newData.id);

    priceItems[indexData] = { ...newData };
    dataFlightPrice.value.data.flightPrices[indexData] = { ...newData };
    handleFormatData();
    emits('submitCruiseQuote');
  }
};

const validateTotalNumOfSeats = (totalNumberOfSeats: number, totalOptionPax: number) => {
  return totalNumberOfSeats <= totalOptionPax;
};

const filterDataFlights = (dataFlights: any) => {
  return dataFlights.find((data: any) => data.type === 'Inbound');
};

const onCloseAirPax = (airData: any, cruiseIndex: number, flightIndex: number) => {
  dataFlightPax.value[cruiseIndex].flightPrices[flightIndex].parties = airData.data.parties.map((item: any) => ({
    adtPassengers: item.adtPassengers,
    chdPassengers: item.chdPassengers,
    email: item.email
  }));
  handleFormatData();
  emits('submitCruiseQuote');
};

const checkFlightDestination = (data: any) => {
  return data.findLast((element: any) => element.type === 'Outbound');
};

const addPax = (dataOption: any, component: any, cruiseIndex: number, index: number, retDate: any, callback = undefined, hotelType = '') => {
  emits('addPax', {
    dataOption: dataOption,
    component: component,
    cruiseIndex: cruiseIndex,
    index: index,
    retDate: retDate,
    callback: callback,
    hotelType: hotelType
  });
};

const handleSaveAir = (dialogRef: any, value: any) => {
  let totalEconomyAdultsSeats = 0;
  let totalEconomyChildrenSeats = 0;
  let totalBusinessAdultsSeats = 0;
  let totalBusinessChildrenSeats = 0;

  const flightIndex = dataFlightPrice.value.index;

  data.value[flightIndex].flightPrices.forEach((priceItem: any) => {
    if (priceItem.type === 'Economy') {
      totalEconomyAdultsSeats += priceItem.adtNo;
      totalEconomyChildrenSeats += priceItem.chdNo;
    } else {
      totalBusinessAdultsSeats += priceItem.adtNo;
      totalBusinessChildrenSeats += priceItem.chdNo;
    }
  });
  dialogRef.close();

  dataFlightPax.value[flightIndex].flights[0].adt = value?.flights?.[0]?.adt;
  dataFlightPax.value[flightIndex].flights[0].chd = value?.flights?.[0]?.chd;
  dataFlightPax.value[flightIndex].flights[0].businessAdt = value?.flights?.[0]?.businessAdt;
  dataFlightPax.value[flightIndex].flights[0].businessChd = value?.flights?.[0]?.businessChd;

  if (!validateTotalNumOfSeats(totalEconomyAdultsSeats, value.adtNo) || !validateTotalNumOfSeats(totalEconomyChildrenSeats, value.chdNo)) {
    for (const index in data.value[flightIndex].flightPrices) {
      if (data.value[flightIndex].flightPrices[index].type === 'Economy') {
        data.value[flightIndex].flightPrices[index].adtNo = 0;
        data.value[flightIndex].flightPrices[index].chdNo = 0;
      }
    }
  }

  if (!validateTotalNumOfSeats(totalBusinessAdultsSeats, value.businessAdtNo) || !validateTotalNumOfSeats(totalBusinessChildrenSeats, value.businessChdNo)) {
    for (const index in data.value[flightIndex].flightPrices) {
      if (data.value[flightIndex].flightPrices[index].type === 'Business') {
        data.value[flightIndex].flightPrices[index].adtNo = 0;
        data.value[flightIndex].flightPrices[index].chdNo = 0;
      }
    }
  }

  dataFlightPrice.value.data.flightPrices = data.value[flightIndex].flightPrices;
  handleFormatData();
  emits('submitCruiseQuote');
};

const openEditModal = (data: any) => {
  const dialogRef = dialog.open(UpdatePassengerDetailModal, {
    props: {
      header: t('book_form.updates_passenger_details'),
      modal: true,
      style: {
        'min-width': '50vw'
      }
    },
    data: {
      passengerData: data,
      isCruise: true
    },
    emits: {
      isSave: (value: any) => {
        handleSaveAir(dialogRef, value);
      }
    }
  });
};

const handleFormatData = () => {
  data.value = cloneDeep([props.dataFlightPrice.data]);
  data.value?.forEach((item: any, index: number) => {
    item.key = `item${index}`;
    item.flightPrices.forEach((p, priceIndex) => {
      p.id = `${index}${priceIndex}`;
    });
  });
};

onBeforeMount(() => {
  handleFormatData();
});

onMounted(() => {
  data.value?.forEach((element: any, itemIndex: number) => {
    expandedRows.value = { ...expandedRows.value, [`item${itemIndex}`]: true };
  });
});
</script>
<template>
  <div v-if="props.dataFlightPrice.data.flights?.length">
    <DataTable :value="data" :sortOrder="0" editMode="row" dataKey="key" v-model:editingRows="airEditingRows" v-model:expandedRows="expandedRows" class="editable-cells-table bg-blue-50 shadow-3" responsiveLayout="stack">
      <TableColumn :expander="true" class="w-3rem" />
      <TableColumn field="option" :header="$t('book_form.option')">
        <template #body="slotProps"> {{ $t('book_form.option') }} {{ slotProps.index + 1 }} </template>
      </TableColumn>
      <TableColumn field="itinerary" :header="$t('worksheet.type')">
        <template #body="{ data }">
          {{ $t(`advance_quote.form.${data.itinerary.toLowerCase()}`) }}
        </template>
      </TableColumn>
      <TableColumn field="gate" :header="$t('book_form.from')" class="max-w-12rem">
        <template #body="{ data }">
          {{ data.flights[0].gate }}
        </template>
      </TableColumn>
      <TableColumn field="dest" :header="$t('book_form.to')" class="max-w-12rem">
        <template #body="{ data }">
          {{ checkFlightDestination(data.flights).dest }}
        </template>
      </TableColumn>
      <TableColumn field="depDate" :header="$t('book_form.departure')">
        <template #body="{ data }">
          {{ formatDate(data.flights[0].depDate, 'dd-LLL-yyyy', true, currentLanguage) }}
        </template>
      </TableColumn>
      <TableColumn field="adt" :header="$t('book_form.adults')">
        <template #body="{ data }">
          {{ (parseInt(data.flights[0].adt, 10) ?? 0) + (parseInt(data.flights[0].businessAdt, 10) ?? 0) }}
        </template>
      </TableColumn>
      <TableColumn field="chd" :header="$t('book_form.children')">
        <template #body="{ data }">
          {{ (parseInt(data.flights[0].chd, 10) ?? 0) + (parseInt(data.flights[0].businessChd, 10) ?? 0) }}
        </template>
      </TableColumn>
      <TableColumn v-if="isManagement" :header="$t('common.edit')" class="py-3">
        <template #body="{ data }">
          <i class="pi pi-pencil cursor-pointer" @click="openEditModal(data)"></i>
        </template>
      </TableColumn>

      <template #expansion="slotProps">
        <DataTable
          :value="slotProps.data.flightPrices"
          data-key="id"
          :sortOrder="0"
          editMode="row"
          v-model:editingRows="editingRows"
          @row-edit-save="onRowEditSaveFlight($event, slotProps.data.flightPrices, dataFlightPrice.data)"
          class="editable-cells-table bg-blue-50 shadow-2"
          responsiveLayout="stack"
        >
          <TableColumn field="type" :header="$t('book_form.air_class')" class="py-3">
            <template #body="{ data }">
              {{ data.type }}
              <span v-if="data.numberOfPassengers">
                <span>
                  - {{ (data.fareBasis?.split(' / ') ?? []).map((part: string) => part.charAt(0)).join('/') }}
                  <span class="font-italic">
                    ({{
                      $t('booking.maximum_count_pax', {
                        count: data.numberOfPassengers
                      })
                    }})
                  </span>
                </span>
              </span>
              <span v-else> - N/A</span>
            </template>
          </TableColumn>
          <TableColumn field="adtNo" :header="$t('book_form.adults')">
            <template #body="{ data, field }">
              {{ data[field] || 0 }}
            </template>
            <template #editor="{ data, field }">
              <InputNumber :max="1000" :min="0" v-model="data[field]" autofocus />
            </template>
          </TableColumn>
          <TableColumn field="chdNo" :header="$t('book_form.children')">
            <template #body="{ data, field }">
              {{ data[field] || 0 }}
            </template>
            <template #editor="{ data, field }">
              <InputNumber :max="1000" :min="0" v-model="data[field]" autofocus />
            </template>
          </TableColumn>
          <TableColumn v-if="hasEditBookPermission && !checkPriorityStatus(priorityStatus, isManagement)" :header="$t('common.edit')" :rowEditor="true" class="py-3 min-w-8rem">
            <template #body="{ data, editorInitCallback }">
              <Button :disabled="!data.numberOfPassengers" icon="pi pi-pencil" class="p-button-secondary p-datatable-row-editor-init" @click="editorInitCallback($event)" />
            </template>
            <template #editor="{ data, editorSaveCallback, editorCancelCallback }">
              <Button :disabled="!data.numberOfPassengers" icon="pi pi-check" class="p-button-secondary p-datatable-row-editor-save" @click="editorSaveCallback($event)" />
              <Button :disabled="!data.numberOfPassengers" icon="pi pi-times" class="p-button-secondary p-datatable-row-editor-cancel" @click="editorCancelCallback($event)" />
            </template>
          </TableColumn>
          <TableColumn v-if="hasViewOccupacyPermission" :header="$t('common.action')" class="py-3 text-center min-w-8rem">
            <template #body="{ data, index }">
              <Button
                :label="isManagement ? $t('book_form.edit_pax') : $t('book_form.add_pax')"
                @click="addPax(data, AirPaxName, dataFlightPrice.index, index, filterDataFlights(dataFlightPrice.data.flights)?.depDate, onCloseAirPax, '')"
                class="p-button-outlined air-add-pax"
              />
            </template>
          </TableColumn>
        </DataTable>
      </template>
    </DataTable>
  </div>
</template>
<style lang="scss" scoped>
:deep(.text-center .p-column-header-content) {
  justify-content: center;
}
.pre-line {
  white-space: pre-line;
}
.min-w-8rem {
  min-width: 8rem;
}
.max-w-12rem {
  max-width: 12rem;
}
</style>
